// Hook for managing customer profile documents with PII scanning
import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'
import { scanFileForPII } from '@/lib/pii-upload-guard'
import type { CustomerProfileDocument } from '@/types/customer'
import type { PIIDetectionResult } from '@/types/pii'

interface UploadData {
  file: File
  title: string
  description?: string
}

export function useCustomerDocuments(customerId: string) {
  const { user } = useAuth()
  const [documents, setDocuments] = useState<CustomerProfileDocument[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [piiResult, setPiiResult] = useState<PIIDetectionResult | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const skipPiiScanRef = useRef(false)

  const loadDocuments = useCallback(async () => {
    if (!user || !customerId) return
    setIsLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('customer_profile_documents')
        .select('*')
        .eq('customer_id', customerId)
        .eq('organization_id', user.organizationId)
        .order('created_at', { ascending: false })
      if (fetchError) throw fetchError
      setDocuments((data || []) as CustomerProfileDocument[])
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }, [user, customerId])

  useEffect(() => { loadDocuments() }, [loadDocuments])

  const upload = useCallback(async (data: UploadData) => {
    if (!user || !customerId) throw new Error('Not authenticated')

    setIsUploading(true)
    setError(null)
    try {
      // PII scan (skip if already approved)
      if (!skipPiiScanRef.current) {
        setIsScanning(true)
        const pii = await scanFileForPII(data.file)
        setIsScanning(false)
        // Always show approval — user must confirm
        setPiiResult(pii || { pii_found: [], total_found: 0, has_critical: false, redacted_text: '' })
        setIsUploading(false)
        return null
      }
      skipPiiScanRef.current = false

      const storagePath = `customers/${customerId}/${Date.now()}_${data.file.name}`
      const { error: uploadError } = await supabase.storage
        .from('customer-documents').upload(storagePath, data.file)
      if (uploadError) throw uploadError

      const { error: insertError } = await supabase
        .from('customer_profile_documents')
        .insert({
          organization_id: user.organizationId,
          customer_id: customerId,
          file_name: data.file.name,
          file_type: data.file.type,
          file_size_bytes: data.file.size,
          storage_path: storagePath,
          storage_bucket: 'customer-documents',
          title: data.title,
          description: data.description || null,
          uploaded_by: user.id,
          category: 'other',
          processing_status: 'pending',
        } as never)
      if (insertError) throw insertError

      // Trigger processing
      try {
        await supabase.functions.invoke('process-document', {
          body: { filePath: storagePath, customerId, organizationId: user.organizationId },
        })
      } catch { /* non-fatal */ }

      await loadDocuments()
      return true
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setIsUploading(false)
    }
  }, [user, customerId, loadDocuments])

  /** Call after PII approval to proceed with upload (skips PII scan) */
  const uploadAfterApproval = useCallback(async (data: UploadData) => {
    skipPiiScanRef.current = true
    setPiiResult(null)
    return upload(data)
  }, [upload])

  const deleteDocument = useCallback(async (docId: string) => {
    if (!user) throw new Error('Not authenticated')
    setIsDeleting(true)
    setError(null)
    try {
      const doc = documents.find((d) => d.id === docId)
      if (doc) {
        await supabase.storage.from(doc.storage_bucket).remove([doc.storage_path])
          .catch(() => {})
      }
      const { error: deleteError } = await supabase
        .from('customer_profile_documents').delete()
        .eq('id', docId).eq('organization_id', user.organizationId)
      if (deleteError) throw deleteError
      await loadDocuments()
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setIsDeleting(false)
    }
  }, [user, documents, loadDocuments])

  return {
    documents, isLoading, isUploading, isDeleting, isScanning,
    piiResult, clearPiiResult: () => setPiiResult(null),
    error, loadDocuments, upload, uploadAfterApproval, deleteDocument,
  }
}
